<template>
  <div
    class="price-scroll-indicator"
    :class="{ 'price-scroll-indicator--sticky': sticky }"
    dir="ltr"
  >
    <div
      ref="track"
      class="indicator-track"
      role="navigation"
      :aria-label="$l('priceScrollNav')"
    >
      <button
        v-for="bar in bars"
        :key="bar.index"
        type="button"
        class="indicator-bar tw-pressable"
        :class="{
          'indicator-bar--empty': !bar.count,
          'indicator-bar--pop': poppedBar === bar.index,
          'indicator-bar--active': activeBucketIndex === bar.index,
        }"
        :style="barStyle(bar)"
        :aria-label="formatBarRange(bar)"
        @click="onBarClick(bar)"
      />

      <div
        v-if="activeBucketIndex != null"
        class="indicator-active-line"
        :style="activeLineStyle"
      />
    </div>

    <div class="indicator-axis" dir="ltr">
      <span
        v-for="(tick, i) in axisTicks"
        :key="i"
        class="indicator-axis__tick"
        :style="{ left: `${tick.pct}%` }"
      >
        {{ tick.label }}
      </span>
    </div>
  </div>
</template>

<script>
import {
  buildHistogramBuckets,
  extractPricedProducts,
  getHistogramPriceBounds,
  getPriceBucketColor,
  priceToBucketIndex,
  bucketCenterPercent,
} from '@/utils/priceHistogram';
import { getLowestStorePrice, getProductKey } from '@/utils/priceUtils';
import { getRelevantProductsForHistogram } from '@/utils/productFilters';

const MOBILE_BUCKET_COUNT = 12;
const TRACK_HEIGHT = 40;
const MIN_BAR_PX = 4;

export default {
  name: 'PriceScrollIndicator',
  props: {
    products: {
      type: Array,
      default: () => [],
    },
    priceBounds: {
      type: Object,
      default: () => ({ min: 0, max: 1000 }),
    },
    currencySign: {
      type: String,
      default: '$',
    },
    sticky: {
      type: Boolean,
      default: true,
    },
    scrollSpy: {
      type: Boolean,
      default: true,
    },
    hideAccessories: {
      type: Boolean,
      default: false,
    },
    searchQuery: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      activeBucketIndex: null,
      poppedBar: null,
      popTimer: null,
      scrollRaf: null,
    };
  },
  computed: {
    boundsMin() {
      return this.histogramBounds.min || 0;
    },
    boundsMax() {
      return Math.max(this.histogramBounds.max || 1000, this.boundsMin + 1);
    },
    histogramBounds() {
      return getHistogramPriceBounds(this.relevantProducts);
    },
    relevantProducts() {
      return getRelevantProductsForHistogram(
        this.products,
        this.hideAccessories,
        this.searchQuery
      );
    },
    histogram() {
      const pricedProducts = extractPricedProducts(this.relevantProducts);
      return buildHistogramBuckets(
        pricedProducts,
        { min: this.boundsMin, max: this.boundsMax },
        MOBILE_BUCKET_COUNT,
      );
    },
    bars() {
      const { buckets, bucketCount } = this.histogram;
      const maxCount = Math.max(...buckets.map((bucket) => bucket.count), 1);

      return buckets.map((bucket) => {
        const priceRatio = bucketCount > 1
          ? bucket.index / (bucketCount - 1)
          : 0;

        return {
          ...bucket,
          priceRatio,
          maxCount,
          fill: getPriceBucketColor(priceRatio, {
            empty: bucket.count === 0,
            density: bucket.densityRatio,
          }),
        };
      });
    },
    axisTicks() {
      const tickCount = 5;
      const span = this.boundsMax - this.boundsMin;
      return Array.from({ length: tickCount }, (_, i) => {
        const ratio = i / (tickCount - 1);
        const value = this.boundsMin + span * ratio;
        return {
          pct: ratio * 100,
          label: this.formatAxisPrice(value),
        };
      });
    },
    priceByKey() {
      const map = {};
      this.relevantProducts.forEach((product) => {
        const key = getProductKey(product);
        if (key) {
          map[key] = getLowestStorePrice(product);
        }
      });
      return map;
    },
    activeLineStyle() {
      if (this.activeBucketIndex == null) {
        return { display: 'none' };
      }
      const pct = bucketCenterPercent(this.activeBucketIndex, MOBILE_BUCKET_COUNT);
      return { left: `${pct}%` };
    },
  },
  watch: {
    products: {
      deep: true,
      handler() {
        this.$nextTick(() => this.updateActiveFromScroll());
      },
    },
  },
  mounted() {
    if (this.scrollSpy) {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.$nextTick(() => this.updateActiveFromScroll());
    }
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll);
    if (this.popTimer) clearTimeout(this.popTimer);
    if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
  },
  methods: {
    barStyle(bar) {
      const maxBarPx = TRACK_HEIGHT - 4;
      const heightPx = bar.count > 0
        ? Math.max((bar.count / bar.maxCount) * maxBarPx, MIN_BAR_PX + 4)
        : MIN_BAR_PX + 2;

      return {
        flex: '1 1 0',
        height: `${heightPx}px`,
        background: bar.fill,
        minWidth: '8px',
        alignSelf: 'flex-end',
      };
    },
    formatBarRange(bar) {
      const min = Math.round(bar.min);
      const max = Math.round(bar.max);
      if (min === max) return `${this.currencySign}${min}`;
      return `${this.currencySign}${min} – ${this.currencySign}${max}`;
    },
    formatAxisPrice(value) {
      return `${this.currencySign}${Math.round(value)}`;
    },
    onScroll() {
      if (this.scrollRaf) return;
      this.scrollRaf = requestAnimationFrame(() => {
        this.scrollRaf = null;
        this.updateActiveFromScroll();
      });
    },
    updateActiveFromScroll() {
      const cards = Array.from(document.querySelectorAll('[data-product-key]'));
      if (!cards.length) {
        this.activeBucketIndex = null;
        return;
      }

      const markerY = window.innerHeight * 0.28;
      let closest = null;
      let closestDist = Infinity;

      cards.forEach((card) => {
        const rect = card.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) return;
        const dist = Math.abs(rect.top - markerY);
        if (dist < closestDist) {
          closestDist = dist;
          closest = card;
        }
      });

      if (!closest) return;

      const price = this.priceByKey[closest.dataset.productKey];
      if (!Number.isFinite(price)) return;

      this.activeBucketIndex = priceToBucketIndex(
        price,
        { min: this.boundsMin, max: this.boundsMax },
        MOBILE_BUCKET_COUNT,
      );
    },
    triggerHaptic() {
      if (navigator.vibrate) {
        navigator.vibrate(12);
      }
    },
    triggerPop(index) {
      this.poppedBar = index;
      if (this.popTimer) clearTimeout(this.popTimer);
      this.popTimer = setTimeout(() => {
        this.poppedBar = null;
      }, 350);
    },
    onBarClick(bar) {
      this.triggerHaptic();
      this.triggerPop(bar.index);
      this.activeBucketIndex = bar.index;

      this.$emit('bar-click', {
        min: bar.min,
        max: bar.max,
        firstProductKey: bar.firstProductKey,
      });
    },
  },
};
</script>

<style scoped>
.price-scroll-indicator {
  width: 100%;
  padding: 6px 0 2px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-bottom: 1px solid #e2e8f0;
  direction: ltr;
}

.price-scroll-indicator--sticky {
  position: sticky;
  top: 52px;
  z-index: 35;
}

.indicator-track {
  position: relative;
  display: flex;
  align-items: flex-end;
  gap: 3px;
  height: 40px;
  padding: 0 4px;
  direction: ltr;
}

.indicator-bar {
  border: 0;
  border-radius: 3px 3px 1px 1px;
  cursor: pointer;
  padding: 0;
  transition: transform 0.15s ease, filter 0.15s ease, box-shadow 0.15s ease;
  -webkit-tap-highlight-color: transparent;
}

.indicator-bar:hover {
  filter: brightness(1.08);
}

.indicator-bar--empty {
  box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.35);
}

.indicator-bar--active {
  box-shadow: 0 0 0 2px rgba(15, 23, 42, 0.25);
}

.indicator-bar--pop {
  animation: bar-pop 0.35s cubic-bezier(0.34, 1.4, 0.64, 1);
}

@keyframes bar-pop {
  0% { transform: scaleY(1); }
  40% { transform: scaleY(1.2); filter: brightness(1.15); }
  100% { transform: scaleY(1); }
}

.indicator-active-line {
  position: absolute;
  top: 2px;
  bottom: 2px;
  width: 2px;
  margin-left: -1px;
  border-radius: 1px;
  background: #0f172a;
  box-shadow: 0 0 6px rgba(15, 23, 42, 0.35);
  pointer-events: none;
  transition: left 0.25s cubic-bezier(0.22, 1, 0.36, 1);
}

.indicator-axis {
  position: relative;
  height: 16px;
  margin-top: 2px;
  padding: 0 4px;
  direction: ltr;
}

.indicator-axis__tick {
  position: absolute;
  transform: translateX(-50%);
  font-size: 8px;
  font-weight: 600;
  color: #64748b;
  white-space: nowrap;
  line-height: 1;
}

.indicator-axis__tick:first-child {
  transform: translateX(0);
}

.indicator-axis__tick:last-child {
  transform: translateX(-100%);
}
</style>
